// =============================================
// MIS PAGOS - PROPIETARIO
// Historial de pagos del propietario
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ownerPortalService from '../../services/ownerPortalService';
import ImageViewer from '../Common/ImageViewer';
import api from '../../services/api';

const OwnerPayments = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3);

  useEffect(() => {
    loadSystemParameters();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [year]);

  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/system-parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');
      
      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await ownerPortalService.getPayments(year);
      setPayments(response.payments || []);
      setTotal(response.total || 0);
      setError('');
    } catch (err) {
      setError('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReceipt = (receiptPath) => {
    let imageUrl;
    if (receiptPath.startsWith('/uploads/')) {
      imageUrl = `http://localhost:3001${receiptPath}`;
    } else if (receiptPath.startsWith('http')) {
      imageUrl = receiptPath;
    } else {
      imageUrl = `http://localhost:3001/uploads/payments/${receiptPath}`;
    }
    setSelectedImage(imageUrl);
    setImageViewerOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const getMonthName = (month) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[month - 1];
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: searchYearsBack }, (_, i) => currentYear - i);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/owner/dashboard')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4">
          Mis Pagos
        </Typography>
      </Box>

      {/* Filtro de Año */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Año</InputLabel>
              <Select
                value={year}
                label="Año"
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Paper sx={{ p: 2, bgcolor: 'primary.light' }}>
              <Typography variant="h6" color="white">
                Total Pagado en {year}: {formatCurrency(total)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha de Pago</TableCell>
                <TableCell>Período</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell align="center">Verificado</TableCell>
                <TableCell align="center">Comprobante</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${getMonthName(payment.month)} ${payment.year}`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="success.main">
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {payment.digital_receipt_url ? (
                      <IconButton 
                        color="secondary"
                        onClick={() => window.open(`http://localhost:3001${payment.digital_receipt_url}`, '_blank')}
                        title="Ver Recibo Oficial (PDF)"
                      >
                        <ReceiptIcon color="error" />
                      </IconButton>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {payment.payment_proof_url ? (
                      <IconButton 
                        color="primary"
                        onClick={() => handleViewReceipt(payment.payment_proof_url)}
                        title="Ver Comprobante Subido"
                      >
                        <ViewIcon />
                      </IconButton>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {payments.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No hay pagos registrados para {year}
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}

      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={selectedImage}
      />
    </Container>
  );
};

export default OwnerPayments;
